import React from 'react';
import { Alert, Box, Chip, Tooltip, Typography } from '@mui/material';

/**
 * At-a-glance answer to "can a customer actually buy this?".
 *
 * A paid plan or add-on with no Stripe price looks completely normal in every
 * other column and fails only at checkout, so the state is worth its own column
 * rather than being buried in the edit dialog.
 */
export const StripeLinkCell = ({
  amount,
  stripePriceId,
}: {
  amount: string | number;
  stripePriceId?: string | null;
}) => {
  if (!(Number(amount) > 0)) {
    return (
      <Tooltip title="Free tier — assigned by hand, never purchased, so it needs no Stripe price.">
        <Chip label="Not sold" size="small" variant="outlined" />
      </Tooltip>
    );
  }

  if (!stripePriceId) {
    return (
      <Tooltip title="No Stripe price, so checkout will fail. Open Edit and save to create one.">
        <Chip label="Not linked" size="small" color="error" />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={stripePriceId}>
      <Chip label="Linked" size="small" color="success" variant="outlined" />
    </Tooltip>
  );
};

interface StripeCatalogFieldProps {
  /** The current form value for the manual override. */
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  /** Ids already stored on the row. Null/undefined when creating. */
  savedPriceId?: string | null;
  savedProductId?: string | null;
  /** Amount currently in the form, so free tiers can be explained differently. */
  amount: number;
  currency: string;
  /** True when the amount or currency in the form differs from what was saved. */
  willReprice?: boolean;
  /** 'plan' bills monthly; 'addon' is a one-off charge. */
  kind: 'plan' | 'addon';
}

const mono = { fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' as const };

/**
 * Replaces the old free-text "Stripe Price Id" input.
 *
 * That field asked the admin to go to the Stripe dashboard, hand-build a
 * matching product and price, and paste the id back. Every plan therefore
 * depended on someone getting a 30-character opaque string right, and a wrong
 * or missing one only surfaced when a customer hit checkout. The server now
 * creates and maintains the Stripe objects itself, so the normal path here is
 * read-only.
 *
 * The manual override is kept, collapsed, because pointing a plan at a price
 * that already exists in Stripe is a real need (migrations, promo pricing set
 * up by finance) and removing it would just push people back to editing the
 * database by hand.
 */
const StripeCatalogField = ({
  value,
  onChange,
  savedPriceId,
  savedProductId,
  amount,
  currency,
  willReprice,
  kind,
}: StripeCatalogFieldProps) => {
  const isFree = !(Number(amount) > 0);

  return (
    <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {isFree ? (
        <Alert severity="info" sx={{ py: 0.5 }}>
          The price is {String(amount || 0)}, so nothing will be created in Stripe. Use this for
          trial or enterprise tiers that are assigned by hand rather than purchased.
          {savedPriceId ? ' The existing Stripe price will be archived on save.' : ''}
        </Alert>
      ) : savedPriceId ? (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Stripe price
          </Typography>
          <Typography sx={mono}>{savedPriceId}</Typography>
          {savedProductId && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Stripe product
              </Typography>
              <Typography sx={mono}>{savedProductId}</Typography>
            </>
          )}
        </Box>
      ) : (
        <Alert severity="info" sx={{ py: 0.5 }}>
          A Stripe product and a {kind === 'plan' ? 'monthly recurring' : 'one-off'} price for{' '}
          {currency} {amount} will be created automatically when you save. You do not need to touch
          the Stripe dashboard.
        </Alert>
      )}

      {willReprice && savedPriceId && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          Stripe prices cannot be edited, so saving this will create a <strong>new</strong> price and
          archive the current one. Customers already{' '}
          {kind === 'plan' ? 'subscribed keep paying the old amount' : 'charged are unaffected'} —
          Stripe does not move them across. Only new purchases use the new price.
        </Alert>
      )}
    </Box>
  );
};

export default StripeCatalogField;
